import KitchenOrderCard from '../KitchenOrderCard';

import {
  RestaurantKitchenOrderDTO,
  RestaurantKitchenOrderStatus,
  RestaurantKitchenStationPrintState,
} from '../../../../types/restaurant/RestaurantKitchenOrderTypes';

interface KitchenBoardGridProps {
  orders: RestaurantKitchenOrderDTO[];
  stationId: number | '';
  actionKey: string | null;
  printStates: Record<number, RestaurantKitchenStationPrintState[]>;
  parallelPreparation: boolean;
  showTimer: boolean;
  focus?: boolean;
  showOrderActions?: boolean;
  showItemActions?: boolean;
  onStart: (id: number) => void;
  onReady: (id: number) => void;
  onStartItem: (orderId: number, itemId: number) => void;
  onReadyItem: (orderId: number, itemId: number) => void;
  onReprint: (id: number) => void;
}

interface Lane {
  status: Extract<
    RestaurantKitchenOrderStatus,
    'PENDING' | 'IN_PREPARATION' | 'READY'
  >;
  label: string;
  accent: string;
  chip: string;
}

const LANES: Lane[] = [
  {
    status: 'PENDING',
    label: 'Pendientes',
    accent: 'bg-warning',
    chip: 'bg-warning/15 text-warning',
  },
  {
    status: 'IN_PREPARATION',
    label: 'Preparando',
    accent: 'bg-primary',
    chip: 'bg-primary/15 text-primary',
  },
  {
    status: 'READY',
    label: 'Listas',
    accent: 'bg-success',
    chip: 'bg-success/15 text-success',
  },
];

const KitchenBoardGrid = ({
  orders,
  stationId,
  actionKey,
  printStates,
  parallelPreparation,
  showTimer,
  focus = false,
  showOrderActions = true,
  showItemActions = true,
  onStart,
  onReady,
  onStartItem,
  onReadyItem,
  onReprint,
}: KitchenBoardGridProps) => {
  const gridClass = focus
    ? 'grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5'
    : 'grid grid-cols-1 items-start gap-5 md:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4';

  return (
    <div className={focus ? 'space-y-6' : 'space-y-8'}>
      {LANES.map((lane) => {
        const laneOrders = orders.filter((order) => order.status === lane.status);

        return (
          <section key={lane.status}>
            <header className="mb-4 flex items-center gap-3">
              <span className={`h-5 w-1.5 rounded-full ${lane.accent}`} />
              <h3 className="text-sm font-bold uppercase tracking-wide text-black dark:text-white">
                {lane.label}
              </h3>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${lane.chip}`}
              >
                {laneOrders.length}
              </span>
            </header>

            {laneOrders.length === 0 ? (
              <p className="rounded-lg border border-dashed border-stroke px-4 py-6 text-center text-xs text-bodydark2 dark:border-strokedark">
                Sin comandas en esta sección.
              </p>
            ) : (
              <div className={gridClass}>
                {laneOrders.map((order) => (
                  <KitchenOrderCard
                    key={order.id}
                    order={order}
                    stationId={stationId}
                    actionKey={actionKey}
                    printStates={printStates[order.id] ?? []}
                    parallelPreparation={parallelPreparation}
                    showTimer={showTimer}
                    focus={focus}
                    showOrderActions={showOrderActions}
                    showItemActions={showItemActions}
                    onStart={onStart}
                    onReady={onReady}
                    onStartItem={onStartItem}
                    onReadyItem={onReadyItem}
                    onReprint={onReprint}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default KitchenBoardGrid;
